import {ErrorHandler, Injectable, Injector} from '@angular/core';
import { MonitoringService } from '../monitoring/monitoring.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';

export type AnyType = any;

export interface IAnyObject {
  [key: string]: AnyType;
  [key: number]: AnyType;
}

export interface IOirHTTPErrorResponse {
  ok: boolean;
  status: number;
  statusText: string;
  url: string;
  message: string;
  error?: any;
}

export function isOirHTTPErrorResponse(
  error: IAnyObject | null
): error is IOirHTTPErrorResponse {
  return !!(
    error &&
    (error as IOirHTTPErrorResponse).statusText &&
    (error as IOirHTTPErrorResponse).url
  );
}

export type OirErrorType =
  | OirError
  | IOirHTTPErrorResponse
  | HttpErrorResponse
  | Error;

export class OirError implements Error {
  readonly name = 'OirError';
  readonly message: string;
  readonly cause: string;

  constructor(message: string, cause: string = null) {
    this.message = message;
    this.cause = cause;
  }
}

export class OirHTTPErrorResponse implements Error {
  public readonly name = 'HTTPError';
  public readonly code: string;
  public readonly message: string;
  public readonly cause: any = '';

  constructor(error: IOirHTTPErrorResponse) {
    this.code = `${error.status}`;
    this.message = error.message;
    this.cause = error.error;
  }
}

@Injectable({ providedIn: 'root' })
export class OirErrorHandler extends ErrorHandler {
  constructor(private injector: Injector) {
    super();
  }

  handleError(error: any): void {
    const monitoringService = this.injector.get(MonitoringService);
    monitoringService.logError(error);
    super.handleError(error);
  }

  static handleHttpError(error: HttpErrorResponse): Observable<IAnyObject> {
    if (isOirHTTPErrorResponse(error)) {
      return throwError(new OirHTTPErrorResponse(error));
    } else {
      return throwError(error);
    }
  }

  static displayError(err: OirErrorType): void {
    console.log('ERROR:', err);
  }
}

export class OirDisplayError {
  readonly code: string;
  readonly message: string;
  readonly cause: IAnyObject | string;

  constructor(error: OirErrorType) {
    if (error instanceof OirError) {
      this.message = error.message;
      this.cause = error.cause;
    } else if (error instanceof OirHTTPErrorResponse) {
      this.code = error.code;
      this.message = error.message;
    } else if (error instanceof HttpErrorResponse) {
      this.message = error.message;
    } else if (error instanceof Error) {
      this.message = error.message;
      this.cause = error.stack;
    } else {
      console.error('OirError :: unknown error type :: ', error);
    }
  }
}
